/**
 * DJ Live Voter Identity Utility
 * Phase DL-5C: Shared-WiFi Voting Correction
 * 
 * Generates and manages a persistent, opaque browser identifier for voting.
 * This allows multiple devices on the same WiFi to vote independently,
 * while preventing duplicate votes from the same browser.
 * 
 * SECURITY NOTES:
 * - The voter_id is client-generated and stored in localStorage
 * - It is NOT cryptographically signed or server-issued
 * - Users CAN clear localStorage to get a new ID (known limitation)
 * - Users CAN use multiple browsers to vote multiple times (known limitation)
 * - This provides reasonable protection against casual duplicate voting
 * - It is NOT fraud-proof and should not be described as such
 */

const VOTER_ID_KEY = 'dj_live_voter_id';

/**
 * Generate a random voter ID using crypto API
 * Format: v1_XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 * The "v1_" prefix allows future versioning if needed
 */
function generateVoterId() {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `v1_${crypto.randomUUID()}`;
  }
  
  // Fallback for older browsers
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  const hex = Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  return `v1_${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}

/**
 * Get the voter ID from localStorage, creating one if it doesn't exist
 * @returns {string} The voter ID
 */
export function getVoterId() {
  try {
    let voterId = localStorage.getItem(VOTER_ID_KEY);
    
    if (!voterId) {
      voterId = generateVoterId();
      localStorage.setItem(VOTER_ID_KEY, voterId);
    }
    
    return voterId;
  } catch (e) {
    // localStorage might be unavailable (private browsing, etc.)
    // Fall back to session-only ID
    console.warn('[VoterID] localStorage unavailable, using session-only ID');
    if (!window._sessionVoterId) {
      window._sessionVoterId = generateVoterId();
    }
    return window._sessionVoterId;
  }
}

/**
 * Check if a voter ID looks valid
 * @param {string} voterId - The voter ID to validate
 * @returns {boolean} Whether the ID appears valid
 */
export function isValidVoterId(voterId) {
  if (!voterId || typeof voterId !== 'string') {
    return false;
  }
  
  // Must start with version prefix
  if (!voterId.startsWith('v1_')) {
    return false;
  }
  
  // Must be reasonable length (v1_ + UUID = about 40 chars)
  if (voterId.length < 30 || voterId.length > 50) {
    return false;
  }
  
  return true;
}

/**
 * Clear the voter ID (mainly for testing)
 */
export function clearVoterId() {
  try {
    localStorage.removeItem(VOTER_ID_KEY);
  } catch (e) {
    // Ignore
  }
  if (window._sessionVoterId) {
    delete window._sessionVoterId;
  }
}

export default { getVoterId, isValidVoterId, clearVoterId };
